import { supabase } from '@/integrations/supabase/client';

/**
 * Busca todos os IDs numéricos dos consultores do time de um supervisor
 * @param supervisorUserId - UUID do usuário supervisor
 * @returns Array com os IDs numéricos dos consultores (incluindo o próprio supervisor)
 */
export async function getConsultorIdsDoTimeSupervisor(supervisorUserId: string | undefined): Promise<number[]> {
  if (!supervisorUserId) return [];
  
  try {
    // Buscar o consultor supervisor
    const { data: supervisor, error: supervisorError } = await supabase
      .from('consultores')
      .select('id')
      .eq('user_id', supervisorUserId)
      .eq('tipo', 'supervisor')
      .eq('status', 'ativo')
      .single();
    
    if (supervisorError || !supervisor) {
      console.error('Erro ao buscar ID do supervisor:', supervisorError);
      return [];
    }
    
    // Buscar todos os membros do time (IDs numéricos)
    const { data: membros, error: membrosError } = await supabase
      .from('consultores')
      .select('id')
      .eq('supervisor_id', supervisor.id)
      .eq('status', 'ativo');
    
    if (membrosError) {
      console.error('Erro ao buscar IDs dos membros do time:', membrosError);
      return [];
    }
    
    const consultorIds = (membros as any[]).map(m => m.id).filter(Boolean);
    consultorIds.push(supervisor.id); // Incluir o próprio supervisor
    
    return consultorIds;
  } catch (error) {
    console.error('Erro ao buscar IDs do time do supervisor:', error);
    return [];
  }
}

/**
 * Busca todos os membros do time de um supervisor
 * @param supervisorUserId - UUID do usuário supervisor
 * @returns Array com os UUIDs dos membros do time (incluindo o próprio supervisor)
 */
export async function getMembrosDoTimeSupervisor(supervisorUserId: string | undefined): Promise<string[]> {
  if (!supervisorUserId) return [];
  
  try {
    // Buscar o consultor supervisor
    const { data: supervisor, error: supervisorError } = await supabase
      .from('consultores')
      .select('id, user_id')
      .eq('user_id', supervisorUserId)
      .eq('tipo', 'supervisor')
      .eq('status', 'ativo')
      .single();
    
    if (supervisorError || !supervisor) {
      console.error('Erro ao buscar supervisor:', supervisorError);
      return [];
    }
    
    // Buscar todos os membros do time (líderes e relacionadores)
    const { data: membros, error: membrosError } = await supabase
      .from('consultores')
      .select('user_id')
      .eq('supervisor_id', supervisor.id)
      .eq('status', 'ativo');
    
    if (membrosError) {
      console.error('Erro ao buscar membros do time:', membrosError);
      return [];
    }
    
    // Retornar array com os UUIDs dos membros + o próprio supervisor
    const userIds = (membros as any[]).map(m => m.user_id).filter(Boolean);
    userIds.push(supervisorUserId); // Incluir o próprio supervisor
    
    console.log('Membros do time do supervisor:', userIds);
    return userIds;
  } catch (error) {
    console.error('Erro ao buscar membros do time do supervisor:', error);
    return [];
  }
}

/**
 * Verifica se um usuário é supervisor
 * @param userId - UUID do usuário
 * @returns true se for supervisor, false caso contrário
 */
export async function isSupervisor(userId: string | undefined): Promise<boolean> {
  if (!userId) return false;
  
  try {
    const { data, error } = await supabase
      .from('consultores')
      .select('tipo')
      .eq('user_id', userId)
      .eq('tipo', 'supervisor')
      .eq('status', 'ativo')
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Erro ao verificar se é supervisor:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Erro ao verificar se é supervisor:', error);
    return false;
  }
}
